import { collection, doc, limit, query } from "firebase/firestore";
import { Map, OrderedMap } from "immutable";
import { collectionData, docData } from "rxfire/firestore";
import { BehaviorSubject } from "rxjs";
import { map } from "rxjs/operators";

import { firestore } from "../firebase";

// Store for sweat equity config per project
export const sweatEquityConfigStore = new BehaviorSubject(Map());

// Store for sweat equity contributions per project
export const sweatEquityContributionsStore = new BehaviorSubject(Map());

let configData = Map();
let contributionsData = Map();

export function clear() {
  configData = Map();
  contributionsData = Map();
  sweatEquityConfigStore.next(Map());
  sweatEquityContributionsStore.next(Map());
}

// Collection paths
const configCollectionName = (projectId) =>
  `projects/${projectId}/sweat-equity-config`;

const contributionsCollectionName = (projectId) =>
  `projects/${projectId}/sweat-equity-contributions`;

// Get config data for a project
export function getConfigData(projectId) {
  return configData.get(projectId) || null;
}

// Get contributions data for a project
export function getContributionsData(projectId) {
  const projectContributions = contributionsData.get(projectId);
  if (!projectContributions) return [];
  return projectContributions.toArray().map(([, item]) => item);
}

// Subscribe to sweat equity config for a project
export function subscribeSweatEquityConfig(projectId) {
  // Simple query - just get the config doc (there should only be one)
  const configRef = query(
    collection(firestore, configCollectionName(projectId)),
    limit(1)
  );

  return collectionData(configRef, { idField: "id" }).pipe(
    map((items) => {
      if (items.length > 0) {
        configData = configData.set(projectId, items[0]);
        sweatEquityConfigStore.next(configData);
        return items[0];
      }
      return null;
    })
  );
}

// Subscribe to sweat equity contributions for a project
export function subscribeSweatEquityContributions(projectId) {
  // Fetch all contributions without ordering (ordering in JS to include docs missing contributionDate)
  const contributionsRef = collection(
    firestore,
    contributionsCollectionName(projectId)
  );

  return collectionData(contributionsRef, { idField: "id" }).pipe(
    map((items) => {
      // Filter out deleted items
      const filtered = items.filter(
        (item) => !item.metadata || !item.metadata.deleted
      );

      // Sort by contributionDate descending in JS (handles missing dates)
      const sorted = filtered.sort((a, b) => {
        const dateA = a.contributionDate?.toDate?.() || a.contributionDate || 0;
        const dateB = b.contributionDate?.toDate?.() || b.contributionDate || 0;
        return dateB - dateA;
      });

      const itemsMap = OrderedMap(sorted.map((i) => [i.id, i]));
      contributionsData = contributionsData.set(projectId, itemsMap);
      sweatEquityContributionsStore.next(contributionsData);

      return sorted;
    })
  );
}

// Get single config document by ID
export function getConfigById(projectId, configId) {
  const docRef = doc(firestore, configCollectionName(projectId), configId);
  return docData(docRef, { idField: "id" });
}

// Get single contribution document by ID
export function getContributionById(projectId, contributionId) {
  const docRef = doc(
    firestore,
    contributionsCollectionName(projectId),
    contributionId
  );
  return docData(docRef, { idField: "id" });
}
